import { useEffect, useRef, useState } from 'react'
import {
  beginOAuth,
  subscribeConnectionAuthorized,
} from '../../api/ws'
import { bridge } from '../../bridge'
import type {
  ConnectionConfigField,
  ConnectionConfigSchema,
  PluginCredentialSpec,
  Secret,
} from '../../store/types'

// Shared building blocks for the connection wizards. Both the
// household-level "Connect an account" flow (ConnectAccountWizard) and
// the per-agent flow (NewConnectionWizard) compose these — the steps are
// identical up to authentication; only what happens with the resulting
// identity differs (bind to an agent vs. stop at the household level).
//
// Keep these presentational and free of agent/connection lifecycle
// concerns. The one exception is OAuthConnectStep, which owns the
// begin_oauth → wait-for-callback dance because that logic is fiddly
// (subscribe-before-open, stale-intent filtering) and not worth
// duplicating; it hands the resulting identity id back to the caller,
// which decides what to do with it.

// ---------- auth-scheme badge ----------

const AUTH_SCHEME_LABELS: Record<string, string> = {
  service_account: 'Service Account',
  oauth2_user: 'Connected Account',
  api_key: 'API Key',
  none: 'No Auth',
}

const AUTH_SCHEME_EXPLAINERS: Record<string, string> = {
  service_account:
    'Requires a Google Cloud service account JSON key with domain-wide delegation. Not suitable for personal Google accounts.',
  oauth2_user:
    'Connect an account via OAuth. The credential belongs to this connection; to use a second account, make a second connection.',
  api_key: 'Requires a static API key.',
  none: 'No credentials required.',
}

export function AuthSchemeBadge({ scheme }: { scheme: string }) {
  const label = AUTH_SCHEME_LABELS[scheme] ?? scheme
  const isOAuth = scheme === 'oauth2_user'
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 4,
        background: isOAuth ? 'var(--tint)' : 'var(--label-quaternary, rgba(60,60,67,0.18))',
        color: isOAuth ? '#fff' : 'var(--label-secondary)',
        marginLeft: 6,
        verticalAlign: 'middle',
      }}
    >
      {label}
    </span>
  )
}

export function authSchemeExplainer(scheme: string): string {
  return AUTH_SCHEME_EXPLAINERS[scheme] ?? ''
}

// ---------- details step (slug + display name) ----------

export function DetailsStep({
  connectionSlug,
  displayName,
  onChangeSlug,
  onChangeName,
  onNext,
  disabled,
  nextLabel = 'Continue',
}: {
  connectionSlug: string
  displayName: string
  onChangeSlug: (v: string) => void
  onChangeName: (v: string) => void
  onNext: (e: React.FormEvent) => void
  disabled: boolean
  nextLabel?: string
}) {
  return (
    <form onSubmit={onNext} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>
          Slug <span style={{ color: '#900' }}>*</span>
        </span>
        <span style={{ fontSize: 11, opacity: 0.55, lineHeight: 1.3 }}>
          A short, unique name used in CLI commands (e.g. <code>github_work</code>).
          Letters, digits, and underscores only.
        </span>
        <input
          value={connectionSlug}
          onChange={(e) => onChangeSlug(e.target.value)}
          placeholder="e.g. github_work"
          required
          style={{ fontSize: 16 }}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Display name (optional)</span>
        <input
          value={displayName}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g. Work GitHub"
          style={{ fontSize: 16 }}
        />
      </label>
      <button
        type="submit"
        disabled={disabled || !connectionSlug.trim()}
        style={{ padding: '10px 12px', fontSize: 16, marginTop: 4 }}
      >
        {nextLabel}
      </button>
    </form>
  )
}

// ---------- config step (schema-driven fields) ----------

export function ConfigStep({
  schema,
  values,
  onChange,
  onNext,
  disabled,
  submitLabel = 'Continue',
}: {
  schema: ConnectionConfigSchema
  values: Record<string, string>
  onChange: (v: Record<string, string>) => void
  onNext: (e: React.FormEvent) => void
  disabled: boolean
  // Label for the submit button. The wizard leaves it "Continue"; the
  // post-creation config editor overrides it to "Save configuration".
  submitLabel?: string
}) {
  const required = new Set(schema.required ?? [])
  return (
    <form onSubmit={onNext} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Object.entries(schema.properties ?? {}).map(([key, field]) => (
        <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            {key}
            {required.has(key) ? <span style={{ color: '#900' }}> *</span> : null}
            {field.description ? (
              <span style={{ opacity: 0.5, marginLeft: 6 }}>— {field.description}</span>
            ) : null}
          </span>
          {field.enum ? (
            <select
              value={values[key] ?? ''}
              onChange={(e) => onChange({ ...values, [key]: e.target.value })}
              style={{ fontSize: 16 }}
            >
              <option value="">Select…</option>
              {field.enum.map((v) => (
                <option key={String(v)} value={String(v)}>{String(v)}</option>
              ))}
            </select>
          ) : field.type === 'boolean' ? (
            <input
              type="checkbox"
              checked={values[key] === 'true'}
              onChange={(e) => onChange({ ...values, [key]: e.target.checked ? 'true' : 'false' })}
              style={{ alignSelf: 'flex-start' }}
            />
          ) : (
            <input
              type={field.type === 'integer' || field.type === 'number' ? 'number' : 'text'}
              value={values[key] ?? ''}
              onChange={(e) => onChange({ ...values, [key]: e.target.value })}
              required={required.has(key)}
              style={{ fontSize: 16 }}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
          )}
        </label>
      ))}
      <button
        type="submit"
        disabled={disabled}
        style={{ padding: '10px 12px', fontSize: 16, marginTop: 4 }}
      >
        {submitLabel}
      </button>
    </form>
  )
}

// ---------- credential step (paste new / use existing secret) ----------

export function CredentialStep({
  spec,
  credIndex,
  totalSecretCreds,
  value,
  onChange,
  existingSecrets,
  pickedSecretID,
  onPickSecret,
  onNext,
  disabled,
  nextLabel = 'Continue',
}: {
  spec: PluginCredentialSpec
  credIndex: number
  totalSecretCreds: number
  value: string
  onChange: (v: string) => void
  existingSecrets: Secret[]
  pickedSecretID: string | null
  onPickSecret: (id: string) => void
  onNext: (e: React.FormEvent) => void
  disabled: boolean
  nextLabel?: string
}) {
  const [reveal, setReveal] = useState(false)
  const [mode, setMode] = useState<'existing' | 'new'>(
    existingSecrets.length > 0 ? 'existing' : 'new',
  )

  const canContinue = mode === 'existing' ? !!pickedSecretID : !!value

  return (
    <form onSubmit={onNext} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {totalSecretCreds > 1 && (
        <div style={{ fontSize: 12, opacity: 0.55 }}>
          Credential {credIndex + 1} of {totalSecretCreds}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 600 }}>{spec.name}</div>
      {spec.description && (
        <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.4 }}>{spec.description}</div>
      )}

      {existingSecrets.length > 0 && (
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' }}>
          {(['existing', 'new'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                flex: 1,
                padding: '8px 0',
                fontSize: 13,
                background: mode === m ? '#007aff' : '#f5f5f7',
                color: mode === m ? '#fff' : '#333',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {m === 'existing' ? 'Use existing' : 'Paste new'}
            </button>
          ))}
        </div>
      )}

      {mode === 'existing' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {existingSecrets.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.55 }}>No secrets found on this host.</div>
          ) : (
            existingSecrets.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onPickSecret(s.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: pickedSecretID === s.id ? '2px solid #007aff' : '1px solid #ddd',
                  background: pickedSecretID === s.id ? '#f0f7ff' : '#fff',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500 }}>{s.name}</div>
                {s.purpose && (
                  <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2 }}>{s.purpose}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {mode === 'new' && (
        <>
          {spec.wizard_guide && (
            <div style={{ fontSize: 13, background: '#f5f5f7', borderRadius: 8, padding: '10px 12px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {spec.wizard_guide}
            </div>
          )}
          {spec.wizard_link && (
            <a
              href={spec.wizard_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 14, color: '#007aff', textDecoration: 'none' }}
            >
              {spec.wizard_link_label ?? spec.wizard_link} ↗
            </a>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Token value <span style={{ color: '#900' }}>*</span>
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type={reveal ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                style={{ fontSize: 16, fontFamily: 'monospace', flex: 1 }}
                placeholder="Paste token here"
              />
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                style={{ fontSize: 12 }}
              >
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>
        </>
      )}

      <button
        type="submit"
        disabled={disabled || !canContinue}
        style={{ padding: '10px 12px', fontSize: 16 }}
      >
        {nextLabel}
      </button>
    </form>
  )
}

// ---------- OAuth connect step ----------

// OAuthConnectStep opens the provider's auth URL and waits for the server
// to broadcast connection_authorized after the user authorizes. On
// success it calls onConnected() — the caller decides what to do next (bind
// it to an agent, or just finish). It does NOT bind anything itself.
//
// Running this against a connection that already holds a credential
// re-authorizes it in place: the server rewrites the same secret's
// ciphertext, so agent bindings and their secret.use rules survive
// untouched. There is no separate "rotate" mode to opt into.
export function OAuthConnectStep({
  connectionID,
  hostID,
  provider,
  pluginDisplayName,
  onConnected,
  onError,
  connectLabel,
  helpText,
}: {
  connectionID: string
  hostID: string
  provider: string
  pluginDisplayName: string
  onConnected: () => void | Promise<void>
  onError: (msg: string) => void
  connectLabel?: string
  helpText?: string
}) {
  const [status, setStatus] = useState<'idle' | 'opening' | 'waiting' | 'saving'>('idle')
  const unsubRef = useRef<(() => void) | null>(null)

  // Clean up subscription on unmount.
  useEffect(() => () => { unsubRef.current?.() }, [])

  async function handleConnect() {
    setStatus('opening')
    onError('')
    try {
      const resp = await beginOAuth({
        resourceConnectionID: connectionID,
        hostID,
        provider,
        label: `${pluginDisplayName} account`,
      })
      if (resp.error || !resp.auth_url) {
        onError(resp.error ?? 'OAuth start failed')
        setStatus('idle')
        return
      }
      // Subscribe before opening the window so we don't miss a fast
      // callback. The replay on WS reconnect may include stale intents
      // from earlier wizard sessions — filter by connectionID and
      // re-register if it's not ours.
      const registerSub = () => {
        unsubRef.current = subscribeConnectionAuthorized(async (msg) => {
          if (msg.resource_connection_id !== connectionID) {
            registerSub()
            return
          }
          unsubRef.current = null
          setStatus('saving')
          await onConnected()
        })
      }
      registerSub()
      // On native WKWebView, window.open is blocked for async calls;
      // use the bridge to open in Safari instead.
      if (bridge.isNative) {
        bridge.call('app.openURL', { url: resp.auth_url }).catch(() => {
          window.open(resp.auth_url, '_blank')
        })
      } else {
        window.open(resp.auth_url, '_blank', 'noopener')
      }
      setStatus('waiting')
    } catch (e) {
      onError(String(e))
      setStatus('idle')
    }
  }

  const label =
    status === 'opening' ? 'Opening…'
    : status === 'waiting' ? 'Waiting for authorization…'
    : status === 'saving' ? 'Saving…'
    : (connectLabel ?? `Connect ${pluginDisplayName} account`)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
      <p style={{ fontSize: 14, color: 'var(--label-secondary)', margin: 0, lineHeight: 1.5 }}>
        {helpText ??
          "You'll be taken to Google to sign in. Hearth will store an access token so your agents can act on your behalf. You can disconnect at any time from the connection's settings."}
      </p>
      <button
        style={{
          padding: '12px',
          fontSize: 16,
          fontWeight: 600,
          background: status === 'idle' ? 'var(--tint)' : 'var(--label-quaternary, rgba(60,60,67,0.18))',
          color: status === 'idle' ? '#fff' : 'var(--label-secondary)',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          cursor: status === 'idle' ? 'pointer' : 'default',
        }}
        disabled={status !== 'idle'}
        onClick={handleConnect}
      >
        {label}
      </button>
      {status === 'waiting' && (
        <p style={{ fontSize: 13, color: 'var(--label-secondary)', margin: 0, textAlign: 'center' }}>
          Complete the sign-in in the browser window, then return here.
        </p>
      )}
    </div>
  )
}

// ---------- pure helpers ----------

export function parseConfigSchema(raw: string | undefined): ConnectionConfigSchema | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as ConnectionConfigSchema
  } catch {
    return null
  }
}

export function parseCredentialSpecs(raw: string | undefined): PluginCredentialSpec[] {
  if (!raw) return []
  try {
    return JSON.parse(raw) as PluginCredentialSpec[]
  } catch {
    return []
  }
}

export function buildConfigJSON(
  schema: ConnectionConfigSchema | null,
  values: Record<string, string>,
): string {
  if (!schema?.properties) return '{}'
  const out: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(schema.properties)) {
    const raw = values[key]
    if (raw === undefined || raw === '') continue
    out[key] = coerceFieldValue(field, raw)
  }
  return JSON.stringify(out)
}

// slugify mirrors the server's slugifyConnectionName: lowercase, replace
// runs of non-alphanumeric chars with "_", strip leading/trailing "_".
export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'connection'
}

function coerceFieldValue(field: ConnectionConfigField, raw: string): unknown {
  switch (field.type) {
    case 'integer': {
      const n = parseInt(raw, 10)
      return Number.isNaN(n) ? raw : n
    }
    case 'number': {
      const n = parseFloat(raw)
      return Number.isNaN(n) ? raw : n
    }
    case 'boolean':
      return raw === 'true'
    default:
      return raw
  }
}

// oauthProviderFromSpecs reads the OAuth provider slug out of a plugin's
// credential specs: `type: oauth2_sonos` → "sonos". Returns '' when the plugin
// has no OAuth credential, which is the signal to show no Connect affordance.
//
// This used to be `plugin_slug.includes('google') ? 'google' : ''`, which was
// honest when Google was the only OAuth provider and wrong the moment a second
// one existed — a Sonos connection would have offered no Connect button at all,
// and the wizard would have run the dance against Google's endpoints.
//
// The slug is the manifest's to declare, for the same reason the daemon and
// relay derive it the same way: it is the one string that ties the plugin, the
// server's provider registry, and the deploy env's <SLUG>_OAUTH_CLIENT_ID
// together.
export function oauthProviderFromSpecs(specs: PluginCredentialSpec[]): string {
  for (const spec of specs) {
    const t = spec.type ?? ''
    if (t.startsWith('oauth2_') && t.length > 'oauth2_'.length) {
      return t.slice('oauth2_'.length)
    }
  }
  return ''
}
